import React, { useEffect, useMemo, useState } from 'react';
import '../../styles/hub-v2.css';
import '../../styles/learn-panel.css';
import APIService from '../../services/APIService';
import { renderMarkdown } from './markdown';
import { IconArrowLeft, IconChevRight } from './icons';

/*
 * PackReader — a subscribed creator pack, read inside the Knowledge Hub.
 *
 * Navigation mirrors the core hub: a rail of the pack's sections and pages on
 * the left, the page itself on the right (intro + tabbed notes), with a
 * prev/next reading pager. A page is for reading — its questions belong to
 * Practice and are not listed here. Page ids are engine concept ids (p.*), so
 * Practice works exactly like any hub concept.
 */

const PackReader = ({ packId, onPractice, onBack }) => {
  const [pack, setPack] = useState(null);
  const [err, setErr] = useState('');
  const [pageId, setPageId] = useState(null);
  const [tabId, setTabId] = useState(null);

  useEffect(() => {
    let alive = true;
    APIService.getPack(packId)
      .then((d) => { if (alive) setPack(d.pack); })
      .catch((e) => { if (alive) setErr(e.message); });
    return () => { alive = false; };
  }, [packId]);

  const pages = useMemo(() => pack?.pages || [], [pack]);
  const bySection = useMemo(() => {
    const by = new Map();
    pages.forEach((p) => {
      const s = p.section || 'Pages';
      if (!by.has(s)) by.set(s, []);
      by.get(s).push(p);
    });
    return [...by.entries()];
  }, [pages]);

  const page = pages.find((p) => p.id === pageId) || pages[0];
  const idx = pages.findIndex((p) => p.id === page?.id);
  const prev = pages[idx - 1];
  const next = pages[idx + 1];
  const tabs = page?.doc?.tabs || [];
  const tab = tabs.find((t) => t.id === tabId) || tabs[0];
  const openPage = (id) => { setPageId(id); setTabId(null); };

  if (err) {
    return (
      <div className="h2-cpage">
        <button type="button" onClick={onBack} className="h2-backbtn"><IconArrowLeft size={14} /> Back to Library</button>
        <div className="h2-loading">{err}</div>
      </div>
    );
  }
  if (!pack) {
    return <div className="h2-loading"><div className="lpn-spinner" /> Opening the pack…</div>;
  }

  return (
    <div className="pkr">
      <nav className="h2-crumbs" aria-label="Breadcrumb">
        <button type="button" onClick={onBack} className="h2-backbtn"><IconArrowLeft size={14} /> Library</button>
        <span className="h2-crumb-trail">
          <b>{pack.title}</b>
          {page && <><IconChevRight size={12} /><b>{page.name}</b></>}
        </span>
      </nav>

      <div className="h2-split">
        <nav className="h2-rail" aria-label="Pack contents">
          <div className="pkr-packhead">
            <div className="pkr-packtitle">{pack.title}</div>
            {pack.creator_name && <div className="pkr-by">by {pack.creator_name}</div>}
          </div>
          {bySection.map(([s, rows]) => (
            <div key={s} className="pkr-section">
              <div className="pkr-section-h">{s}</div>
              {rows.map((p) => (
                <button key={p.id} type="button" className={`h2-sub${p.id === page?.id ? ' on' : ''}`}
                  onClick={() => openPage(p.id)}>
                  <span className="h2-sub-name">{p.name}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>

        <main className="h2-page">
          {!page ? (
            <div className="h2-loading">This pack has no pages yet.</div>
          ) : (
            <div className="h2-cpage in-pack">
              <header className="h2-cpage-head">
                <div>
                  <h2>{page.name}</h2>
                  <div className="h2-cpage-meta">
                    {page.section && <em className="h2-imp imp-core">{page.section}</em>}
                  </div>
                </div>

              </header>

              {page.doc?.intro && <p className="h2-intro big">{page.doc.intro}</p>}
              {tabs.length > 0 && (
                <>
                  <div className="lpn-tabs" role="tablist">
                    {tabs.map((t) => (
                      <button key={t.id} type="button" role="tab" aria-selected={t.id === tab?.id}
                        className={t.id === tab?.id ? 'on' : ''} onClick={() => setTabId(t.id)}>{t.label}</button>
                    ))}
                  </div>
                  {tab?.md ? (
                    <div className="lpn-md h2-notes">{renderMarkdown(tab.md)}</div>
                  ) : (
                    <ul className="lpn-points big">
                      {(tab?.points || []).map((p, i) => (typeof p === 'string' ? (
                        <li key={i}><span className="lpn-dot" />{p}</li>
                      ) : (
                        <li key={i}><span className="lpn-dot" /><span><b>{p.t}</b> — {p.d}</span></li>
                      )))}
                    </ul>
                  )}
                </>
              )}

              <footer className="h2-pager">
                {prev ? (
                  <button type="button" className="h2-ghost" onClick={() => openPage(prev.id)}>← {prev.name}</button>
                ) : <span />}
                {next ? (
                  <button type="button" className="h2-primary" onClick={() => openPage(next.id)}>{next.name} →</button>
                ) : <span />}
              </footer>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default PackReader;
